<template>
  <div class="voip-sidebar" :class="{ 'voip-sidebar-open': isOpen }">
    <div class="voip-sidebar-content">
      <!-- Header da Sidebar -->
      <div class="voip-sidebar-header">
        <h5 class="voip-sidebar-title">
          <i class="fas fa-phone-alt mr-2"></i>
          Gestão do VoIP
        </h5>
        <button
          class="voip-sidebar-close"
          @click="closeSidebar"
          type="button"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Conteúdo da Sidebar -->
      <div class="voip-sidebar-body">
        <!-- Disponibilidade para Ligações -->
        <label class="voip-availability-toggle mb-3">
          <span>Disponível para atender ligações?</span>
          <ToogleSwitch
            v-model="availableForCall"
            @change="availableUpdate"
          />
        </label>

        <!-- Tela de Chamada Recebida -->
        <div v-if="callState === 'receiving'" class="chamada-recebida mb-3">
          <div class="call-header">
            <span class="call-title">
              <i class="fas fa-exclamation-circle"></i> Ligação recebida!
            </span>
          </div>

          <div class="associatedContainer pulsating-box">
            <div class="call-info">
              <span class="callText">Nome: <br> {{ incomingCall.callerName }}</span><br>
              <span class="callText">Número: <br> {{ incomingCall.callerNumber }}</span>
            </div>

            <div class="call-buttons">
              <button class="btn-atender" @click="acceptCall">
                <span><i class="fa fa-phone" style="color: #fff;"></i></span>
              </button>

              <button class="btn-recusar" @click="declineCall">
                <span><i class="fas fa-phone-slash"></i></span>
              </button>
            </div>
          </div>
        </div>

        <!-- Tela de Chamada em Andamento -->
        <div v-if="callState === 'inProgress'" class="chamada-andamento mb-3">
          <div class="call-header">
            <span class="call-title">
              <i class="fas fa-phone-volume"></i> Chamada em andamento
            </span>
          </div>

          <div class="associatedContainer">
            <div class="call-info">
              <span class="callText">Nome: <br> {{ incomingCall.callerName }}</span><br>
              <span class="callText">Número: <br> {{ incomingCall.callerNumber }}</span><br>
              <span class="callText">Tempo em chamada: <br> <strong>{{ formattedCallDuration }}</strong></span>
            </div>

            <button
              class="btn-encerrar"
              @click="endActiveCall"
            >
              <i class="fas fa-phone-slash" style="color: white;"></i> Encerrar chamada
            </button>
          </div>
        </div>

        <!-- Status da Conexão -->
        <div v-if="callState === 'idle'" class="voip-status mb-3">
          <div class="voip-status-indicator" :class="connectionStatusClass">
            <i class="fas fa-circle"></i>
          </div>
          <span class="voip-status-text">{{ connectionStatusText }}</span>
        </div>

        <!-- Formulário de Conexão -->
        <div v-if="!isAgentLoggedIn && callState === 'idle'" class="voip-connection-form">
          <!-- Seleção de Campanha -->
          <div class="form-group mb-3">
            <label class="voip-label">
              Filas: <span class="text-danger">*</span>
            </label>
            <div class="input-group">
              <div class="input-group-prepend">
                <button class="btn btn-outline-secondary" type="button">
                  <i class="fas fa-users"></i>
                </button>
              </div>
              <select
                class="form-control"
                v-model="selectedCampaign"
                :disabled="isConnecting || isLoadingCampaigns"
              >
                <option value="">
                  {{ isLoadingCampaigns ? 'Carregando filas...' : 'Selecione uma opção' }}
                </option>
                <option
                  v-for="campaign in campaigns"
                  :key="campaign.id"
                  :value="campaign.id"
                >
                  {{ campaign.name }}
                </option>
              </select>
            </div>
          </div>

          <!-- Botão Conectar -->
          <button
            class="btn btn-primary voip-connect-btn w-100"
            @click="connectVoip"
            :disabled="!canConnect || isConnecting"
          >
            <i class="fas fa-check mr-2" v-if="!isConnecting"></i>
            <i class="fas fa-spinner fa-spin mr-2" v-else></i>
            {{ isConnecting ? 'Conectando...' : 'Conectar VoIP' }}
          </button>
        </div>

        <!-- Interface de Ligação -->
        <div v-else-if="callState === 'idle'" class="voip-call-interface">
          <!-- Informações da Conexão -->
          <div class="voip-connection-info mb-3">
            <div class="voip-campaign-info">
              <small class="text-muted">Fila:</small>
              <div class="voip-campaign-name">{{ selectedCampaignName }}</div>
            </div>
            <div class="voip-status-info">
              <small class="text-muted">Status:</small>
              <div class="voip-status-message">{{ statusMessage }}</div>
            </div>
          </div>

          <!-- Botão Modo Manual -->
          <div v-if="!isInManualMode" class="form-group mb-3">
            <button
              class="btn btn-warning voip-manual-btn w-100"
              @click="enterManualMode"
              :disabled="!isAgentLoggedIn"
            >
              <i class="fas fa-hand-paper mr-2"></i>
              Entrar no Modo Manual
            </button>
          </div>

          <!-- Interface de Chamadas -->
          <div v-if="isInManualMode">
            <!-- Campo de Número -->
            <div class="form-group mb-3">
              <label class="voip-label">Número para ligar:</label>
              <input
                type="tel"
                class="form-control voip-phone-input"
                v-model="phoneNumber"
                placeholder="(xx) xxxxx-xxxx"
                @input="formatPhoneNumber"
              />
            </div>

            <!-- Botões de Chamada -->
            <div class="voip-actions">
              <button
                class="btn btn-success voip-call-btn"
                @click="makeCall"
                :disabled="!canDial || isCalling"
              >
                <i class="fas fa-phone mr-2" v-if="!isCalling"></i>
                <i class="fas fa-spinner fa-spin mr-2" v-else></i>
                {{ isCalling ? 'Ligando...' : 'Ligar' }}
              </button>

              <button
                v-if="currentCallId"
                class="btn btn-danger voip-hangup-btn"
                @click="hangupCall"
                :disabled="isCalling"
              >
                <i class="fas fa-phone-slash mr-2"></i>
                Desligar
              </button>
            </div>
          </div>

          <!-- Botão Desconectar -->
          <div class="voip-disconnect-section">
            <button
              class="btn btn-danger voip-disconnect-btn w-100"
              @click="disconnectVoip"
            >
              <i class="fas fa-sign-out-alt mr-2"></i>
              Desconectar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ThreeCVoipService from '@/shared/services/voip/ThreeCVoipService'
import AssociateRepository from '@/shared/http/repositories/business/associate'
import ToogleSwitch from '@/components/UI/toogleSwitch/toogleSwitch.vue'

export default {
  name: 'VoipSidebar',
  components: {
    ToogleSwitch
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      // connection
      isConnected: false,
      isConnecting: false,
      isCalling: false,
      isInitialized: false,
      isAgentLoggedIn: false,
      currentAgentId: null,
      isInManualMode: false,
      availableForCall: false,

      // form data
      selectedCampaign: '',
      phoneNumber: '',

      // campaigns list
      campaigns: [],
      isLoadingCampaigns: false,

      // call state
      currentCallId: null,
      statusMessage: 'Inicializando...',

      /* incoming call state
      callState: 'receiving', // 'idle', 'receiving', 'inProgress', 'ended' - TESTE: receiving
      incomingCall: {
        callerName: 'João da Silva',
        callerNumber: '(11) 98765-4321',
        callId: 'TEST-CALL-123',
        campaignId: 1
      }, */

      // incoming call state
      callState: 'idle', // 'idle', 'receiving', 'inProgress', 'ended'
      incomingCall: {
        callerName: '',
        callerNumber: '',
        callId: null,
        campaignId: null,
        isAssociate: false
      },
      callDuration: 0,
      callStartTime: null,
      callTimer: null,
      callAutoDeclineTimeout: null,

      // ringtone
      ringtone: null,
      ringtoneEnabled: false
    }
  },
  computed: {
    canConnect () {
      return this.selectedCampaign && this.isInitialized
    },
    canDial () {
      return this.isInManualMode && this.phoneNumber.trim()
    },
    connectionStatusClass () {
      return {
        'voip-status-connected': this.isAgentLoggedIn,
        'voip-status-connecting': this.isConnecting,
        'voip-status-disconnected': !this.isAgentLoggedIn && !this.isConnecting
      }
    },
    connectionStatusText () {
      if (this.isAgentLoggedIn) return 'Conectado'
      if (this.isConnecting) return 'Conectando...'
      if (!this.isInitialized) return 'Inicializando...'
      return 'Desconectado'
    },
    selectedCampaignName () {
      const campaign = this.campaigns.find(c => c.id === this.selectedCampaign)
      return campaign ? campaign.name : ''
    },
    formattedCallDuration () {
      const minutes = Math.floor(this.callDuration / 60)
      const seconds = this.callDuration % 60
      return minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0')
    }
  },
  mounted () {
    this.initializeVoip()
    this.setupEventListeners()
    this.initializeRingtone()
  },
  beforeDestroy () {
    this.cleanup()
  },
  methods: {
    closeSidebar () {
      this.$emit('close')
    },

    initializeRingtone () {
      try {
        this.ringtone = new Audio('/static/sounds/ringtone_threec.mp3')
        this.ringtone.loop = true
        this.ringtone.volume = 1

        // Tentar habilitar após primeira interação
        const enableRingtone = () => {
          if (this.ringtone && !this.ringtoneEnabled) {
            this.ringtone.play().then(() => {
              this.ringtone.pause()
              this.ringtone.currentTime = 0
              this.ringtoneEnabled = true
              document.removeEventListener('click', enableRingtone)
            }).catch(err => {
              // Ringtone bloqueado - aguardando interação do usuário
              void err
            })
          }
        }

        document.addEventListener('click', enableRingtone, { once: true })
      } catch (error) {
        console.error('❌ Erro ao carregar ringtone:', error)
      }
    },

    async initializeVoip () {
      try {
        this.statusMessage = 'Inicializando integração...'

        await ThreeCVoipService.initialize()

        this.isInitialized = true
        this.statusMessage = 'Pronto para conectar'

        await this.loadCampaigns()
      } catch (error) {
        console.warn('⚠️ VoIP não inicializado:', error.message)
        this.statusMessage = 'Serviço não disponível'
        // Não exibe toast de erro - configuração não disponível é comportamento esperado
      }
    },

    setupEventListeners () {
      ThreeCVoipService.on('initialized', () => {
        this.isInitialized = true
        this.statusMessage = 'Pronto para conectar'
      })

      ThreeCVoipService.on('agent-connected', () => {
        this.isAgentLoggedIn = true
        this.isConnecting = false
        this.currentAgentId = this.getPayloadAgentId(arguments[0])
        this.statusMessage = 'Agente conectado'
        this.$bvToast.toast('Agente conectado ao VoIP!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
      })

      ThreeCVoipService.on('agent-idle', () => {
        this.isAgentLoggedIn = true
        this.isConnecting = false
        this.availableForCall = true
        this.currentAgentId = this.getPayloadAgentId(arguments[0])
        this.statusMessage = 'Agente ocioso - pronto para modo manual'
      })

      ThreeCVoipService.on('manual-mode-entered', () => {
        this.isInManualMode = true
        this.availableForCall = true
        this.statusMessage = 'Modo manual ativado - pronto para discar'
      })

      ThreeCVoipService.on('agent-in-acw', () => {
        this.statusMessage = 'Em pós-atendimento'
      })

      ThreeCVoipService.on('agent-work-break', () => {
        this.availableForCall = false
        this.statusMessage = 'Agente em pausa'
      })

      ThreeCVoipService.on('agent-left-work-break', () => {
        this.availableForCall = true
        this.statusMessage = 'Agente disponível'
      })

      ThreeCVoipService.on('call-created', (payload) => {
        this.handleIncomingCall(payload)
      })

      ThreeCVoipService.on('call-was-answered', (payload) => {
        this.handleCallAnswered(payload)
      })

      ThreeCVoipService.on('call-connected', (payload) => {
        console.log('🎯 VoipSidebar recebeu call-connected')
        this.handleCallConnected(payload)
      })

      ThreeCVoipService.on('call-was-ended', (payload) => {
        this.handleCallEnded(payload)
      })

      ThreeCVoipService.on('call-was-abandoned', (payload) => {
        this.handleCallEnded(payload)
      })

      ThreeCVoipService.on('call-was-not-answered', (payload) => {
        this.handleCallNotAnswered(payload)
      })

      ThreeCVoipService.on('call-finished', (payload) => {
        this.handleCallEnded(payload)
      })

      ThreeCVoipService.on('error', (error) => {
        this.isConnecting = false
        this.isCalling = false

        // Tratamento específico para erro 422
        if (error && error.response && error.response.status === 422) {
          const defaultMsg = 'Não foi possível desconectar. O agente pode já estar desconectado ou em uma chamada ativa.'
          this.statusMessage = 'Erro na operação'
          this.$bvToast.toast(defaultMsg, {
            title: 'Erro',
            variant: 'danger',
            solid: true
          })
          return
        }

        // Verifica se é erro de configuração/credenciais (não exibe toast)
        const isConfigError = error.message && (
          error.message.includes('Credenciais 3C Plus não disponíveis') ||
          error.message.includes('Configuração 3C Plus não está carregada') ||
          error.message.includes('URL da API 3C Plus não configurada') ||
          error.message.includes('Falha ao carregar configuração') ||
          error.message.includes('Usuário não possui 3C Plus habilitado') ||
          error.message.includes('desabilitada globalmente')
        )

        if (isConfigError) {
          this.statusMessage = 'Serviço não disponível'
          console.warn('⚠️ VoIP:', error.message)
        } else {
          this.statusMessage = 'Erro na operação'
          this.$bvToast.toast(error.message || 'Erro na operação', {
            title: 'Erro',
            variant: 'danger',
            solid: true
          })
        }
      })
    },

    async loadCampaigns () {
      this.isLoadingCampaigns = true
      try {
        const campaigns = await ThreeCVoipService.getCampaigns()
        this.campaigns = campaigns
      } catch (error) {
        // Verifica se é erro de configuração (não exibe toast)
        const isConfigError = error.message && (
          error.message.includes('Credenciais') ||
          error.message.includes('Configuração') ||
          error.message.includes('não disponível') ||
          error.message.includes('não está carregada') ||
          error.message.includes('não possui 3C Plus habilitado') ||
          error.message.includes('desabilitada globalmente')
        )

        if (!isConfigError) {
          this.$bvToast.toast('Erro ao carregar filas', {
            title: 'Erro',
            variant: 'danger',
            solid: true
          })
        } else {
          console.warn('⚠️ Filas não carregadas:', error.message)
        }
      } finally {
        this.isLoadingCampaigns = false
      }
    },

    async connectVoip () {
      if (!this.canConnect) return

      this.isConnecting = true
      this.statusMessage = 'Conectando agente...'

      try {
        await ThreeCVoipService.loginAgent(this.selectedCampaign)
        this.statusMessage = 'Aguardando confirmação...'
      } catch (error) {
        console.warn('⚠️ Erro na conexão VoIP:', error.message)
        this.isConnecting = false

        // Verifica se é erro de configuração
        const isConfigError = error.message && (
          error.message.includes('Credenciais') ||
          error.message.includes('Configuração') ||
          error.message.includes('não disponível') ||
          error.message.includes('não possui 3C Plus habilitado') ||
          error.message.includes('desabilitada globalmente')
        )

        if (isConfigError) {
          this.statusMessage = 'Serviço não disponível'
        } else {
          this.statusMessage = 'Erro na conexão'
        }
      }
    },

    async disconnectVoip () {
      try {
        await ThreeCVoipService.logoutAgent()
        this.isAgentLoggedIn = false
        this.isInManualMode = false
        this.currentCallId = null
        this.phoneNumber = ''
        this.statusMessage = 'Desconectado'

        this.$emit('disconnected')

        this.$bvToast.toast('Desconectado do VoIP', {
          title: 'Info',
          variant: 'info',
          solid: true
        })
      } catch (error) {
        console.error('Erro na desconexão:', error)

        // Verificar se é erro 422 (Unprocessable Entity)
        if (error.response && error.response.status === 422) {
          const defaultMsg = 'O agente já estava desconectado, nenhuma ação necessária.'
          const data = error.response && error.response.data ? error.response.data : null
          let responseMessage = null
          if (data) {
            if (data.message) {
              responseMessage = data.message
            } else if (data.error) {
              responseMessage = data.error
            } else if (Array.isArray(data.errors) && data.errors.length > 0) {
              const firstError = data.errors[0]
              if (firstError && firstError.message) {
                responseMessage = firstError.message
              } else if (typeof firstError === 'string') {
                responseMessage = firstError
              }
            } else if (data.detail) {
              responseMessage = data.detail
            }
          }
          this.$bvToast.toast(responseMessage || defaultMsg, {
            title: 'Erro ao Desconectar',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          })
        } else {
          this.$bvToast.toast(error.message || 'Erro ao desconectar', {
            title: 'Erro',
            variant: 'danger',
            solid: true
          })
        }
      }
    },

    async enterManualMode () {
      if (!this.isAgentLoggedIn) return

      try {
        this.statusMessage = 'Entrando no modo manual...'
        await ThreeCVoipService.enterManualMode()
        this.statusMessage = 'Aguardando confirmação do modo manual...'
      } catch (error) {
        this.statusMessage = 'Erro ao entrar no modo manual'
      }
    },

    async availableUpdate (isAvailable) {
      if (!this.isAgentLoggedIn) {
        this.availableForCall = false
        this.$bvToast.toast('Você precisa estar conectado ao VoIP primeiro', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      try {
        if (isAvailable) {
          // Sair da pausa (work break) se estiver pausado
          this.statusMessage = 'Ficando disponível...'
          await ThreeCVoipService.exitWorkBreak()
          ThreeCVoipService.emit('agent-is-idle', { manualToggle: true })
          this.$bvToast.toast('Você está disponível para atender ligações', {
            title: 'Disponível',
            variant: 'success',
            solid: true
          })
        } else {
          // Entrar em pausa (work break)
          this.statusMessage = 'Entrando em pausa...'
          await ThreeCVoipService.enterWorkBreak()
          ThreeCVoipService.emit('agent-entered-work-break', { manualToggle: true })
          this.$bvToast.toast('Você está indisponível para ligações', {
            title: 'Pausado',
            variant: 'info',
            solid: true
          })
        }
      } catch (error) {
        console.error('Erro ao atualizar disponibilidade:', error)
        // Reverte o toggle em caso de erro
        this.availableForCall = !isAvailable

        this.$bvToast.toast(error.message || 'Erro ao atualizar disponibilidade', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },

    formatPhoneNumber (event) {
      let value = event.target.value.replace(/\D/g, '')

      if (value.length <= 11) {
        if (value.length <= 2) {
          this.phoneNumber = value
        } else if (value.length <= 7) {
          this.phoneNumber = `(${value.slice(0, 2)}) ${value.slice(2)}`
        } else {
          this.phoneNumber = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`
        }
      }
    },

    async makeCall () {
      if (!this.canDial || this.isCalling) return

      this.isCalling = true
      this.statusMessage = `Discando para ${this.phoneNumber}...`

      try {
        await ThreeCVoipService.dialNumber(this.phoneNumber)

        // Preparar estado para ligação manual saindo
        this.callState = 'idle' // Mantém idle enquanto disca
        this.incomingCall = {
          callerName: 'Ligação Manual',
          callerNumber: this.phoneNumber,
          callId: null, // Será preenchido quando conectar
          campaignId: this.selectedCampaign,
          isAssociate: false
        }

        this.$emit('call-started', {
          number: this.phoneNumber,
          campaign: this.selectedCampaign
        })

        this.$bvToast.toast(`Ligando para ${this.phoneNumber}`, {
          title: 'Chamada',
          variant: 'info',
          solid: true
        })
      } catch (error) {
        this.isCalling = false
        this.statusMessage = 'Erro ao fazer chamada'
      }
    },

    async hangupCall () {
      if (!this.currentCallId) return

      try {
        this.statusMessage = 'Desligando chamada...'
        await ThreeCVoipService.hangupCall()
        this.statusMessage = 'Chamada desligada'
      } catch (error) {
        this.statusMessage = 'Erro ao desligar chamada'
      }
    },

    // ===== Métodos de Chamadas Recebidas =====
    async handleIncomingCall (payload) {
      console.log('📞 handleIncomingCall disparado. availableForCall:', this.availableForCall, 'payload:', payload)

      if (!this.isAgentLoggedIn) {
        return
      }

      if (!this.isEventForCurrentAgent(payload)) {
        return
      }
      
      if (!this.availableForCall) {
        return
      }

      this.callState = 'receiving'
      const callerNumber = this.getPayloadPhoneNumber(payload) || 'Número não disponível'
      this.incomingCall = {
        callerName: (payload.customer && payload.customer.name) || 'Nome não disponível',
        callerNumber: callerNumber,
        callId: payload.id,
        campaignId: payload.campaignId,
        isAssociate: false
      }

      const associadoNome = await this.getAssociateNameByPhone(callerNumber)
      if (associadoNome) {
        this.incomingCall.callerName = `${associadoNome} (Associado)`
        this.incomingCall.isAssociate = true
      }

      // Tocar ringtone
      this.playRingtone()

      // Auto-decline após 120 segundos
      this.callAutoDeclineTimeout = setTimeout(() => {
        if (this.callState === 'receiving') {
          this.declineCall()
        }
      }, 120000)

      // Notificar usuário
      this.$bvToast.toast('Nova chamada recebida!', {
        title: 'Chamada Entrante',
        variant: 'info',
        solid: true,
        autoHideDelay: 5000
      })
    },

    getPayloadAgentId (payload) {
      if (!payload) return null

      return (payload.agent && (payload.agent.id || payload.agent._id)) ||
        (payload.call && (payload.call.agent_id || payload.call.agentId)) ||
        (payload.call && payload.call.agent && (payload.call.agent.id || payload.call.agent._id)) ||
        null
    },

    isEventForCurrentAgent (payload) {
      const payloadAgentId = this.getPayloadAgentId(payload)
      if (!payloadAgentId || !this.currentAgentId) {
        return true
      }
      return String(payloadAgentId) === String(this.currentAgentId)
    },

    getPayloadPhoneNumber (payload) {
      if (!payload) return null

      return payload.phoneNumber ||
        (payload.call && (payload.call.phone_number || payload.call.number || payload.call.phone)) ||
        (payload.customer && payload.customer.phone) ||
        null
    },

    normalizePhoneNumber (phone) {
      if (!phone) return ''
      const digits = String(phone).replace(/\D/g, '')
      if (digits.length > 11 && digits.startsWith('55')) {
        return digits.slice(2)
      }
      return digits
    },

    async getAssociateNameByPhone (phone) {
      const normalizedPhone = this.normalizePhoneNumber(phone)
      if (!normalizedPhone) return null

      try {
        const response = await AssociateRepository.GetByTelefone(normalizedPhone)
        const associados = response && response.data ? response.data : []
        if (Array.isArray(associados) && associados.length > 0) {
          return associados[0].nome || null
        }
      } catch (error) {
        console.warn('Erro ao buscar associado por telefone:', error)
      }
      return null
    },

    handleCallAnswered (payload) {
      if (payload.id === this.incomingCall.callId) {
        this.clearAutoDeclineTimeout()
        this.callState = 'inProgress'
        this.startCallTimer()
        this.statusMessage = 'Chamada em andamento'
      }
    },

    handleCallConnected (payload) {
      // Detectar ligação manual
      const isManualCall = payload.call && payload.call.call_mode === 'manual'
      const callId = (payload.call && (payload.call.id || payload.call.telephony_id)) || payload.id
      const callerNumber = this.getPayloadPhoneNumber(payload) || this.phoneNumber || 'Número não disponível'
      const callerName = (payload.mailing && payload.mailing.data && payload.mailing.data.COOPERADO) ||
        (payload.customer && payload.customer.name) ||
        this.incomingCall.callerName ||
        'Nome não disponível'

      if (callId) {
        this.currentCallId = callId
      }
      console.log('🔍 isManualCall:', isManualCall)
      console.log('🔍 payload.call:', payload.call)
      console.log('🔍 call_mode:', payload.call ? payload.call.call_mode : 'undefined')
      
      if (isManualCall) {
        // Para ligações manuais, mostrar tela de chamada em andamento
        this.callState = 'inProgress'
        this.incomingCall = {
          callerName: 'Ligação Manual',
          callerNumber: payload.call.phone || this.phoneNumber,
          callId: payload.call.id || payload.call.telephony_id,
          campaignId: payload.call.campaign_id,
          isAssociate: false
        }
        
        this.startCallTimer()
        this.isCalling = false
        this.statusMessage = 'Chamada conectada'

        // Forçar atualização do Vue
        this.$forceUpdate()

        return
      }

      // Para chamadas de discador/receptivas, não há etapa de aceitar
      if (this.callState !== 'inProgress') {
        this.callState = 'inProgress'
        this.startCallTimer()
      }

      this.incomingCall = {
        callerName: callerName,
        callerNumber: callerNumber,
        callId: callId || this.incomingCall.callId,
        campaignId: (payload.call && payload.call.campaign_id) || payload.campaignId || this.selectedCampaign,
        isAssociate: this.incomingCall.isAssociate || false
      }
      this.statusMessage = 'Chamada conectada'
    },

    handleCallEnded (payload) {
      // Verificar por ID direto ou por telephony_id
      const payloadId = payload.id || (payload.call && payload.call.telephony_id)
      const currentCallId = this.incomingCall.callId

      if (payloadId === currentCallId || this.callState === 'inProgress') {
        this.resetCallState()

        this.$bvToast.toast('Chamada finalizada', {
          title: 'Chamada',
          variant: 'info',
          solid: true
        })
      }
    },

    handleCallNotAnswered (payload) {
      // Verificar por ID direto ou por telephony_id
      const payloadId = payload.call && payload.call.telephony_id
      const currentCallId = this.incomingCall.callId
      
      // Se estiver em ligação manual (inProgress ou tem phoneNumber), resetar
      if (this.callState === 'inProgress' || (this.isInManualMode && this.phoneNumber)) {
        this.resetCallState()

        this.$bvToast.toast('Chamada não foi atendida', {
          title: 'Chamada',
          variant: 'warning',
          solid: true
        })
        return
      }

      // Verificar por ID também (para chamadas receptivas)
      if (payloadId === currentCallId) {
        this.resetCallState()

        this.$bvToast.toast('Chamada não foi atendida', {
          title: 'Chamada',
          variant: 'warning',
          solid: true
        })
      }
    },

    acceptCall () {
      if (this.callState !== 'receiving') return
      
      this.stopRingtone()
      this.clearAutoDeclineTimeout()

      // A API 3C Plus não tem endpoint direto para aceitar - a chamada é automaticamente conectada
      // quando o agente está disponível. Mudamos apenas o estado local.
      this.callState = 'inProgress'
      this.startCallTimer()
      this.statusMessage = 'Chamada em andamento'
    },

    declineCall () {
      if (this.callState !== 'receiving') return
      
      this.stopRingtone()
      this.clearAutoDeclineTimeout()

      // Tentar desligar a chamada através da API se possível
      if (this.incomingCall.callId) {
        ThreeCVoipService.hangupCall().catch(err => {
          console.warn('Erro ao desligar chamada recusada:', err)
        })
      }

      this.resetCallState()

      this.$bvToast.toast('Chamada recusada', {
        title: 'Chamada',
        variant: 'warning',
        solid: true
      })
    },

    endActiveCall () {
      if (this.callState !== 'inProgress') return
      
      ThreeCVoipService.hangupCall()
        .then(() => {
          this.resetCallState()
          this.$bvToast.toast('Chamada encerrada', {
            title: 'Chamada',
            variant: 'success',
            solid: true
          })
        })
        .catch(err => {
          console.error('Erro ao encerrar chamada:', err)
          this.resetCallState()
          this.$bvToast.toast('Erro ao encerrar chamada', {
            title: 'Erro',
            variant: 'danger',
            solid: true
          })
        })
    },

    startCallTimer () {
      this.callStartTime = Date.now()
      this.callDuration = 0
      this.callTimer = setInterval(() => {
        this.callDuration = Math.floor((Date.now() - this.callStartTime) / 1000)
      }, 1000)
    },

    clearCallTimer () {
      if (this.callTimer) {
        clearInterval(this.callTimer)
        this.callTimer = null
      }
    },

    clearAutoDeclineTimeout () {
      if (this.callAutoDeclineTimeout) {
        clearTimeout(this.callAutoDeclineTimeout)
        this.callAutoDeclineTimeout = null
      }
    },

    playRingtone () {
      if (this.ringtone) {
        this.ringtone.play()
          .then(() => {
            // Ringtone iniciado com sucesso
          })
          .catch(err => {
            void err
            // Tentar novamente após pequeno delay
            setTimeout(() => {
              if (this.ringtone && this.callState === 'receiving') {
                this.ringtone.play().catch(() => {})
              }
            }, 100)
          })
      }
    },

    stopRingtone () {
      if (this.ringtone) {
        this.ringtone.pause()
        this.ringtone.currentTime = 0
      }
    },

    resetCallState () {
      this.stopRingtone()
      this.callState = 'idle'
      this.callDuration = 0
      this.callStartTime = null
      this.incomingCall = {
        callerName: '',
        callerNumber: '',
        callId: null,
        campaignId: null,
        isAssociate: false
      }
      this.clearCallTimer()
      this.clearAutoDeclineTimeout()
      this.statusMessage = this.isAgentLoggedIn ? 'Agente disponível' : 'Desconectado'
    },

    cleanup () {
      // Remover listeners
      ThreeCVoipService.off('initialized')
      ThreeCVoipService.off('agent-connected')
      ThreeCVoipService.off('agent-idle')
      ThreeCVoipService.off('manual-mode-entered')
      ThreeCVoipService.off('call-connected')
      ThreeCVoipService.off('call-finished')
      ThreeCVoipService.off('call-created')
      ThreeCVoipService.off('call-was-answered')
      ThreeCVoipService.off('call-was-ended')
      ThreeCVoipService.off('call-was-abandoned')
      ThreeCVoipService.off('call-was-not-answered')
      ThreeCVoipService.off('error')

      // Limpar timers
      this.clearCallTimer()
      this.clearAutoDeclineTimeout()

      // Desconectar se estiver conectado
      if (this.isAgentLoggedIn) {
        ThreeCVoipService.disconnect()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.voip-sidebar {
  position: fixed;
  top: 0;
  right: -400px;
  width: 400px;
  height: 100vh;
  background: #fff;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 1050;
  transition: right 0.3s ease;
  border-left: 1px solid #e9ecef;

  &.voip-sidebar-open {
    right: 0;
  }
}

.voip-sidebar-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.voip-sidebar-header {
  background: #04a791;
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #04a791;
}

.voip-sidebar-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.voip-sidebar-close {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
}

.voip-sidebar-body {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
}

.voip-status {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.notification__availability{
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1rem;
  color: white;
  padding: .75rem;
  border-style: solid;
  border-radius: .375rem;
  border-color:#00000029;
  -webkit-box-align: center;
}

.voip-status-indicator {
  margin-right: 0.5rem;

  &.voip-status-connected {
    color: #04a791;
  }

  &.voip-status-connecting {
    color: #ffbb33;
    animation: pulse 1.5s infinite;
  }

  &.voip-status-disconnected {
    color: #607d8b;
  }
}

.voip-status-text {
  font-weight: 500;
  font-size: 0.9rem;
}

.voip-availability-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
  margin: 0;
  font-weight: 500;
  font-size: 0.9rem;
  color: #495057;
}

.voip-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: block;
  font-size: 0.9rem;
}

.voip-connect-btn {
  background: #04a791;
  border-color: #04a791;
  font-weight: 600;
  padding: 0.75rem;

  &:hover:not(:disabled) {
    background: #006a5b;
    border-color: #006a5b;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.voip-connection-info {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.voip-campaign-info {
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.voip-campaign-name {
  font-weight: 600;
  color: #495057;
}

.voip-phone-input {
  font-size: 1.1rem;
  text-align: center;
  font-weight: 500;
}

.voip-actions {
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
}

.voip-call-btn {
  background: #04a791;
  border-color: #04a791;
  font-weight: 600;
  padding: 0.75rem;

  &:hover:not(:disabled) {
    background: #006a5b;
    border-color: #006a5b;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.voip-manual-btn {
  background: #ffbb33; /* brand-warning */
  border-color: #ffbb33; /* brand-warning */
  font-weight: 600;
  padding: 0.75rem;

  &:hover:not(:disabled) {
    background: #e6a429;
    border-color: #e6a429;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.voip-hangup-btn {
  background: #ff4444; /* brand-danger */
  border-color: #ff4444; /* brand-danger */
  font-weight: 600;
  padding: 0.5rem;

  &:hover:not(:disabled) {
    background: #e63939;
    border-color: #e63939;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.voip-disconnect-btn {
  background: #ff4444;
  border-color: #ff4444;
  font-weight: 600;
  padding: 0.5rem;

  &:hover {
    background: #e63939;
    border-color: #e63939;
  }
}

// ===== Estilos de Chamadas =====
.chamada-recebida,
.chamada-andamento {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  min-height: 450px;
  padding: 1rem;
  border-radius: 0.5rem;
  box-sizing: border-box;
  gap: 1rem;
}

.chamada-recebida {
  background: #12406A;
  box-shadow: 0 4px 12px rgba(18, 64, 106, 0.3);
}

.chamada-andamento {
  background: #126a4e;
  box-shadow: 0 4px 12px rgba(18, 106, 78, 0.3);
}

.call-header {
  width: 100%;
  text-align: center;
  margin-bottom: 0.5rem;
}

.call-title {
  color: #fff;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.associatedContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  gap: 1rem;
  flex: 1;
}

.call-info {
  text-align: center;
  width: 100%;
}

.callText {
  color: #fff;
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.6;
  display: block;
  margin-bottom: 0.5rem;

  strong {
    font-weight: 700;
    color: #fff;
  }
}

.call-buttons {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 1rem;
}

.btn-atender,
.btn-recusar,
.btn-encerrar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
  border: none;
  color: #FFFFFF;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 1.5rem;
  min-width: 60px;
  height: 60px;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}

.btn-atender {
  background-color: #24aa28;

  &:hover {
    background-color: #1e8a23;
  }
}

.btn-recusar,
.btn-encerrar {
  background-color: #dc3545;

  &:hover {
    background-color: #c82333;
  }
}

.btn-encerrar {
  width: 100%;
  border-radius: 0.5rem;
  height: auto;
  padding: 0.75rem 1rem;
  gap: 0.5rem;
}

.pulsating-box {
  animation: pulseLight 2s infinite ease-in-out;
}

@keyframes pulseLight {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);
  }
  50% {
    opacity: 0.9;
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.4);
  }
}

.voip-status-info {
  margin-top: 0.5rem;
}

.voip-status-message {
  font-weight: 500;
  color: #495057;
  font-size: 0.85rem;
}

.voip-disconnect-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .voip-sidebar {
    width: 100%;
    right: -100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/voip/VoipSidebar.vue